<template>
  <div class="rounded-4 border border-divider shadow-xs">
    <div
      v-if="!isOpen"
      data-cy="email-header"
      class="grid cursor-pointer grid-cols-6 items-start justify-start p-4"
      @click="isOpen = true"
    >
      <div v-tooltipable class="truncate text-headline">{{ fromNameOnly }}</div>
      <div v-tooltipable class="col-span-4 truncate">
        {{ email.content_text || "-" }}
      </div>
      <div class="flex items-center justify-end">
        <date-time
          v-if="email.created_at"
          :date="email.created_at"
          format="MMM, dd"
          is-fixed
          show-time-if-today
          class="text-xs"
        />
      </div>
    </div>
    <div
      v-if="isOpen"
      class="flex cursor-pointer grid-cols-6 items-start justify-between p-4"
      @click="isOpen = false"
    >
      <div class="col-span-4 flex flex-col space-y-2 gap-x-3 truncate">
        <div class="user-info">
          <thread-icon :icon-info="iconInfo" />
          <email-status :email="email" collapse />
          <links-clicked-tooltip-icon
            v-if="!!email.links_clicked?.length"
            :links-clicked="email.links_clicked"
          />
          <email-avatar-with-name
            :sender="email.sender?.name || email.from"
            text-class="text-label"
          />
          <span
            class="truncate text-xs text-primary"
            data-cy="thread-item-from"
          >
            {{ email.from }}
          </span>
        </div>
        <div class="user-info">
          <span class="text-xs font-medium text-label">
            {{ $t("COMMON.TO").toLowerCase() }}:
          </span>
          <span
            v-if="email.receiver?.name"
            class="text-xs text-label"
            data-cy="thread-item-to"
          >
            {{ email.receiver.name }}
          </span>
          <span class="truncate text-xs text-primary">
            {{ email.to }}
          </span>
        </div>
        <div v-if="email.reply_to" class="user-info">
          <span class="text-xs font-medium text-label">
            {{ $t("EMAIL_INTEGRATION.REPLY_TO").toLowerCase() }}:
          </span>
          <span
            class="truncate text-xs text-primary"
            data-cy="thread-item-reply-to"
          >
            {{ email.reply_to }}
          </span>
        </div>
        <div v-if="email.cc" class="user-info">
          <span class="text-xs font-medium text-label">
            {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CC").toLowerCase() }}:
          </span>
          <span class="text-xs text-primary">
            {{ email.cc }}
          </span>
        </div>
        <div v-if="email.bcc" class="user-info">
          <span class="text-xs font-medium text-label">
            {{ $t("EMAIL_INTEGRATION.BCC").toLowerCase() }}:
          </span>
          <span class="truncate text-xs text-primary">
            {{ email.bcc }}
          </span>
        </div>
      </div>
      <div class="col-span-2 flex items-center space-x-2">
        <date-time
          v-if="email.created_at"
          :date="email.created_at"
          is-fixed
          show-time-if-today
          class="text-xs"
        />
        <reply-to-email
          v-if="
            email.emailable?.application_id &&
            !email.is_outgoing &&
            (email.reply_to || email.from)
          "
          :data="replyData"
        />
      </div>
    </div>
    <div
      v-if="email.content_text && isOpen"
      class="px-4 pb-4 whitespace-break-spaces text-headline"
      data-cy="thread-item-content"
      v-html="email.content_text"
    />
    <div v-if="email.status_message" class="flex space-x-1">
      <icon-base :icon="IconErrorWarning" class="text-error" />
      <span class="text-error">{{ email.status_message }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

import { useEmailLogInfo } from "@/hooks/communicationLogs";

import EmailAvatarWithName from "@/components/communicationLogs/communication/emailThread/EmailAvatarWithName.vue";
import EmailStatus from "@/components/communicationLogs/communication/emailThread/EmailStatus.vue";
import LinksClickedTooltipIcon from "@/components/communicationLogs/communication/emailThread/LinksClickedTooltipIcon.vue";
import ReplyToEmail from "@/components/communicationLogs/communication/emailThread/ReplyToEmail.vue";
import ThreadIcon from "@/components/communicationLogs/communication/threads/ThreadIcon.vue";
import IconErrorWarning from "@/components/icons/IconErrorWarning.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";

import type { EmailThreadItem } from "@/models/communicationLogs";

interface Props {
  email: EmailThreadItem;
  businessName: string | undefined;
  open?: boolean;
}

const { email, businessName, open = false } = defineProps<Props>();

const { iconInfo } = useEmailLogInfo(email);

const isOpen = ref(open);

const fromNameOnly = computed(() => email.from?.split("<")[0] ?? "");

const replyData = computed(() => ({
  emailId: email.id,
  dealId: email.emailable?.application_id || "",
  name: businessName || "",
  subject: email.subject || "",
  to: email.reply_to || email.from || "",
}));
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.user-info {
  @apply flex items-center space-x-2;
}
</style>
